import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useToast } from 'vue-toastification'
import router from '@/router'

// Configuração base da API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'
const API_TIMEOUT = 30000 // 30 segundos

// Criar instância do axios
const api: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

// Interceptor de requisição
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Adicionar token de autenticação se existir
    const token = localStorage.getItem('auth_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Adicionar timestamp para evitar cache
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      }
    }
    
    // Log da requisição em desenvolvimento
    if (import.meta.env.DEV) {
      console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data
      })
    }
    
    return config
  },
  (error: AxiosError) => {
    console.error('Erro na requisição:', error)
    return Promise.reject(error)
  }
)

// Interceptor de resposta
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log da resposta em desenvolvimento
    if (import.meta.env.DEV) {
      console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data
      })
    }
    
    return response
  },
  async (error: AxiosError) => {
    const toast = useToast()
    
    // Log do erro
    if (import.meta.env.DEV) {
      console.error(`❌ ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        status: error.response?.status,
        data: error.response?.data
      })
    }
    
    // Tratar diferentes tipos de erro
    if (error.response) {
      const { status, data } = error.response as { status: number; data: { message?: string; errors?: Record<string, string[]> } }
      
      switch (status) {
        case 401:
          // Token expirado ou inválido
          localStorage.removeItem('auth_token')
          localStorage.removeItem('user_data')
          delete api.defaults.headers.common['Authorization']
          
          if (router.currentRoute.value.name !== 'login') {
            toast.error('Sessão expirada. Faça login novamente.')
            router.push('/login')
          }
          break
          
        case 403:
          toast.error('Você não tem permissão para realizar esta ação.')
          break
          
        case 404:
          toast.error('Recurso não encontrado.')
          break
          
        case 422:
          // Erros de validação
          if (data.errors) {
            const errorMessages = Object.values(data.errors).flat()
            errorMessages.forEach((message: any) => {
              toast.error(message)
            })
          } else {
            toast.error(data.message || 'Dados inválidos.')
          }
          break
          
        case 429:
          toast.error('Muitas tentativas. Tente novamente em alguns minutos.')
          break
          
        case 500:
          toast.error('Erro interno do servidor. Tente novamente mais tarde.')
          break
          
        default:
          toast.error(data.message || 'Erro inesperado. Tente novamente.')
      }
    } else if (error.request) {
      // Erro de rede
      toast.error('Erro de conexão. Verifique sua internet.')
    } else {
      // Erro na configuração da requisição
      toast.error('Erro na requisição. Tente novamente.')
    }
    
    return Promise.reject(error)
  }
)

// Funções auxiliares para diferentes tipos de requisição
export const apiClient = {
  // GET
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return api.get<T>(url, config)
  },
  
  // POST
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return api.post<T>(url, data, config)
  },
  
  // PUT
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return api.put<T>(url, data, config)
  },
  
  // PATCH
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return api.patch<T>(url, data, config)
  },
  
  // DELETE
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return api.delete<T>(url, config)
  },
  
  // Upload de arquivo
  upload: <T = any>(url: string, file: File, onProgress?: (progress: number) => void): Promise<AxiosResponse<T>> => {
    const formData = new FormData()
    formData.append('file', file)
    
    return api.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      }
    })
  },
  
  // Download de arquivo
  download: (url: string, filename?: string): Promise<void> => {
    return api.get(url, {
      responseType: 'blob'
    }).then((response) => {
      const blob = new Blob([response.data])
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename || 'download'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    })
  }
}

// Funções para gerenciar cache
export const cacheManager = {
  // Cache simples em memória
  cache: new Map<string, { data: any; timestamp: number; ttl: number }>(),
  
  get(key: string): any | null {
    const item = this.cache.get(key)
    if (!item) return null
    
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key)
      return null
    }
    
    return item.data
  },
  
  set(key: string, data: any, ttl: number = 300000): void { // 5 minutos por padrão
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    })
  },
  
  delete(key: string): void {
    this.cache.delete(key)
  },
  
  clear(): void {
    this.cache.clear()
  }
}

// Função para requisições com cache
export const cachedRequest = async <T = any>(
  key: string,
  requestFn: () => Promise<AxiosResponse<T>>,
  ttl?: number
): Promise<T> => {
  // Verificar cache primeiro
  const cached = cacheManager.get(key)
  if (cached) {
    return cached
  }
  
  // Fazer requisição
  const response = await requestFn()
  
  // Salvar no cache
  cacheManager.set(key, response.data, ttl)
  
  return response.data
}

// Função para retry automático
export const retryRequest = async <T = any>(
  requestFn: () => Promise<AxiosResponse<T>>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<AxiosResponse<T>> => {
  let lastError: any
  
  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await requestFn()
    } catch (error) {
      lastError = error
      
      if (i < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)))
      }
    }
  }
  
  throw lastError
}

export default api